import { Dayjs } from "dayjs";

export const responseStatus = ['SUCCESS', 'WARNING', 'ERROR', 'INFO'] as const;
export type ResponseStatus = typeof responseStatus[number];

export type LowerCaseResponseStatus = 'success' | 'error' | 'warning' | 'info';

export const responseStatusToLowerCase: Record<ResponseStatus, LowerCaseResponseStatus> = {
    SUCCESS: 'success',
    WARNING: 'warning',
    ERROR: 'error',
    INFO: 'info'
  };

export const section = ['SALES', 'COSTS', 'PROFIT'] as const;
export type Section = typeof section[number];

export const tileName = ['CREDIT', 'DEBT_LEVEL', 'MARKETABLE_ASSET', 'ACCOUNTING_ASSET', 'LOAN', 'EBITDA'] as const;
export type TileName = typeof tileName[number];

export const userStatus = ['A', 'D'] as const;
export type UserStatus = typeof userStatus[number];

export interface ApiResponseEntity<T = {}> {
    data: T;
    message: string;
    code: ResponseStatus;
}

export interface Page<T = {}> {
    content: T;
    pageable: Pageable;
    last: boolean;
    totalPages: number;
    totalElements: number;
    first: boolean;
    size: number;
    number: number;
    sort: Sort,
    numberOfElements: number;
    empty: boolean;
}

export interface Pageable {
    pageNumber: number;
    pageSize: number;
    sort: Sort;
    offset: number;
    paged: boolean;
    unpaged: boolean;
}

export interface Sort {
    empty: boolean;
    sorted: boolean;
    unsorted: boolean;
}

export interface Pair {
    first: number;
    second: number;
}

export interface LoginResponseDTO {
    user: TgaUserDTO;
    token: string;
    expiresIn: number;
}

export interface TgaUserDTO {
    id: number;
    name: string;
    emailAddress: string;
    status: UserStatus;
}

export interface LoginState {
    loading: boolean,
    token?: string,
    user?: TgaUserDTO,
    expirationDate?: Date
};

export interface GlobalState {
    path?: string;
    companies: CompanyDTO[];
    selectedCompanyId: number;
    updateDate: Dayjs;
    period: string;
    dates: [Dayjs, Dayjs];
}

export interface StoreState {
    login: LoginState;
    global: GlobalState;
}

export interface LoginUserDTO {
    email: string;
    password: string;
}

export interface MultiGraphData {
    [key: string]: Pair;
}

export interface GraphData {
    [key: string]: number;
}

export interface RequestDTO {
    companyId: number;
}

export interface BasicRequestDTO extends RequestDTO {
    validFrom: string;
    validTo: string;
}

export interface CostsRequestDTO extends RequestDTO {
    month: number;
    year: number;
}

export interface PageRequestDTO extends BasicRequestDTO {
    page: number;
    size: number;
}

export interface AbstractAudit {
    createdBy: number;
    createdDate: Date;
    modifiedBy?: number;
    modifiedDate?: Date;
}

export interface ExcelImportStatusDTO {
    importStatus: 'OK' | 'ERROR';
    errorMessage: string;
}

export interface CompanyDTO extends AbstractAudit {
    id: number;
    ic: string;
    name: string;
    responsiblePerson: string;
    address: string;
    dic: string;
    phone: string;
    mobilePhone: string;
    emailAddress: string;
    website: string;
}

export interface ImportHistoryDTO {
    id: number;
    type: string;
    importTs: Date;
    status: string;
    error: string;
    runCount: string;
}

export interface GeneralTileDTO {
    tileName: TileName;
    amount?: number;
}

export interface GeneralTileDetailDTO {
    tileName: TileName;
    graphData: GraphData;
}

export interface SectionTileDTO {
    tileName: TileName;
    actualAmount?: number;
    plannedAmount?: number;
    diffAmount?: number;
    graphData: MultiGraphData;
}

export interface TableDataDTO {
    companyId: number;
    companyName: string;
    actualSalesAmount: number;
    plannedSalesAmount: number;
    actualCostsAmount: number;
    plannedCostsAmount: number;
    actualProfitAmount: number;
    plannedProfitAmount: number;
}
